import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApplications, getLoans, getMyReferral, applyReferralCode, getRiskScore } from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { MobileMenuButton, MobileMenu } from '../components/MobileNav';
import { EduProDashboardSection } from '../components/EduProDashboardSection';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BookOpen, FileText, DollarSign, LogOut, Plus, Clock, CheckCircle,
  Share2, Copy, Gift, Users, Shield, AlertTriangle, ArrowRight
} from 'lucide-react';

const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loans, setLoans] = useState([]);
  const [referral, setReferral] = useState(null);
  const [riskScore, setRiskScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [refCode, setRefCode] = useState('');
  const [applyingRef, setApplyingRef] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.role !== 'student') { navigate('/dashboard'); return; }
    fetchAll();
  }, [user, navigate]);

  const fetchAll = async () => {
    try {
      const [appsRes, loansRes, refRes] = await Promise.all([
        getApplications(),
        getLoans(),
        getMyReferral().catch(() => ({ data: { has_referral: false } })),
      ]);
      setApplications(appsRes.data || []);
      setLoans(loansRes.data || []);
      setReferral(refRes.data);

      // Try to load risk score
      if (user?.user_id) {
        try {
          const rsRes = await getRiskScore(user.user_id);
          setRiskScore(rsRes.data);
        } catch (e) { /* no score yet */ }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleApplyRef = async () => {
    if (!refCode.trim()) return;
    setApplyingRef(true);
    try {
      await applyReferralCode(refCode.trim());
      alert('Codigo aplicado! Recibes $500 de descuento en tu primer credito.');
      fetchAll();
      setRefCode('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Codigo no valido');
    }
    setApplyingRef(false);
  };

  const copyRefCode = () => {
    if (referral?.referral_link) {
      navigator.clipboard.writeText(referral.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(p || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const getStatusBadge = (status) => {
    const cfg = {
      draft: { label: 'Borrador', cls: 'bg-slate-100 text-slate-700' },
      submitted: { label: 'Enviada', cls: 'bg-blue-100 text-blue-700' },
      under_review: { label: 'En revision', cls: 'bg-amber-100 text-amber-700' },
      approved: { label: 'Aprobada', cls: 'bg-green-100 text-green-700' },
      rejected: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
      disbursed: { label: 'Desembolsado', cls: 'bg-purple-100 text-purple-700' },
      documents_required: { label: 'Docs pendientes', cls: 'bg-orange-100 text-orange-700' },
    };
    const c = cfg[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <EdFiLogo size="sm" linkTo="/" />
            <div className="hidden md:flex items-center gap-4">
              <Link to="/programs"><Button variant="ghost" size="sm" className="text-slate-600">Catalogo</Button></Link>
              <Link to="/calculator"><Button variant="ghost" size="sm" className="text-slate-600">Calculadora</Button></Link>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="text-sm text-slate-600 hidden sm:block">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="student-logout">
                <LogOut className="w-5 h-5" />
              </Button>
              <MobileMenuButton open={mobileOpen} onToggle={() => setMobileOpen(!mobileOpen)} />
            </div>
          </div>
          <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} links={[
            { to: '/programs', label: 'Catalogo' },
            { to: '/calculator', label: 'Calculadora' },
          ]} />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="student-dashboard-title">Hola, {user?.name?.split(' ')[0]}</h1>
            <p className="text-sm sm:text-base text-slate-600">Gestiona tus solicitudes y creditos</p>
          </div>
          <Link to="/programs">
            <Button className="bg-brand-magenta hover:bg-brand-magenta-dark text-white w-full sm:w-auto" data-testid="new-application-btn">
              <Plus className="w-4 h-4 mr-2" /> Nueva Solicitud
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card><CardContent className="pt-4 sm:pt-6"><div className="flex items-center gap-2 sm:gap-3"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" /></div><div><p className="text-xl sm:text-2xl font-bold" data-testid="student-apps-count">{applications.length}</p><p className="text-xs text-slate-600">Solicitudes</p></div></div></CardContent></Card>
          <Card><CardContent className="pt-4 sm:pt-6"><div className="flex items-center gap-2 sm:gap-3"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" /></div><div><p className="text-xl sm:text-2xl font-bold text-green-600">{applications.filter(a => ['approved', 'disbursed'].includes(a.status)).length}</p><p className="text-xs text-slate-600">Aprobadas</p></div></div></CardContent></Card>
          <Card><CardContent className="pt-4 sm:pt-6"><div className="flex items-center gap-2 sm:gap-3"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center"><DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" /></div><div><p className="text-lg sm:text-xl font-bold text-purple-600">{loans.length}</p><p className="text-xs text-slate-600">Prestamos</p></div></div></CardContent></Card>
          <Card><CardContent className="pt-4 sm:pt-6"><div className="flex items-center gap-2 sm:gap-3"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-pink-100 rounded-lg flex items-center justify-center"><Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600" /></div><div><p className="text-xl sm:text-2xl font-bold text-pink-600">{referral?.total_referred || 0}</p><p className="text-xs text-slate-600">Referidos</p></div></div></CardContent></Card>
        </div>

        {/* EduPro promo */}
        <Link to="/edupro/apply" className="block mb-6 sm:mb-8" data-testid="dashboard-edupro-banner">
          <div className="rounded-2xl bg-gradient-to-r from-[#0A2463] to-[#0d2f7a] text-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-xl px-3 py-2 flex-shrink-0">
                <img src="/edupro-logo.png" alt="EduPro" className="h-7 w-auto" />
              </div>
              <div>
                <p className="font-bold text-base sm:text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Financia tu programa EduPro · 1,800 USD</p>
                <p className="text-white/60 text-xs sm:text-sm">18–24 meses en MXN o COP · con aval · 🇲🇽 🇨🇴</p>
              </div>
            </div>
            <Button className="bg-[#E91E8C] hover:bg-[#c21473] text-white w-full sm:w-auto">
              Solicitar EduPro <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Link>

        {/* Risk Score Card (if available) */}
        {riskScore && (
          <Card className="mb-6 sm:mb-8 border-l-4" style={{ borderLeftColor: riskScore.tier_color }}>
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: riskScore.tier_color }} data-testid="risk-score-badge">
                    {Math.round(riskScore.composite_score)}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Score de Riesgo: {riskScore.tier_label}</p>
                    <p className="text-sm text-slate-600">Tasa ajustada: {riskScore.adjusted_rate_annual}% anual</p>
                  </div>
                </div>
                <p className="text-sm text-slate-500">{riskScore.recommendation}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 sm:mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-student-overview">Solicitudes</TabsTrigger>
            <TabsTrigger value="edupro" data-testid="tab-student-edupro">EduPro</TabsTrigger>
            <TabsTrigger value="loans" data-testid="tab-student-loans">Prestamos</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-student-referrals">Referidos</TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader><CardTitle>Mis Solicitudes</CardTitle></CardHeader>
              <CardContent>
                {applications.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">No tienes solicitudes</h3>
                    <p className="text-slate-600 mb-4">Explora nuestro catalogo y aplica a un programa</p>
                    <Link to="/programs"><Button className="bg-brand-blue">Ver Programas</Button></Link>
                  </div>
                ) : (
                  <div className="table-scroll">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Programa</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="hidden sm:table-cell">Fecha</TableHead>
                        <TableHead></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {applications.map(app => (
                          <TableRow key={app.application_id}>
                            <TableCell className="font-medium max-w-[150px] sm:max-w-none truncate">{app.program_name || app.program_id}</TableCell>
                            <TableCell className="text-right">{formatPrice(app.amount_requested || app.requested_amount)}</TableCell>
                            <TableCell>{getStatusBadge(app.status)}</TableCell>
                            <TableCell className="hidden sm:table-cell">{formatDate(app.created_at)}</TableCell>
                            <TableCell>
                              {app.status === 'draft' && (
                                <Link to={`/apply/${app.program_id}`}>
                                  <Button size="sm" variant="outline" className="text-xs">Continuar</Button>
                                </Link>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EduPro Tab */}
          <TabsContent value="edupro">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2">Financiamiento EduPro <img src="/edupro-logo.png" alt="EduPro" className="h-5 w-auto" /></CardTitle></CardHeader>
              <CardContent>
                <EduProDashboardSection />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Loans Tab */}
          <TabsContent value="loans">
            <Card>
              <CardHeader><CardTitle>Mis Prestamos</CardTitle></CardHeader>
              <CardContent>
                {loans.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <DollarSign className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">No tienes prestamos activos</h3>
                    <p className="text-slate-600">Tus prestamos apareceran aqui una vez aprobados</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {loans.map(loan => (
                      <div key={loan.loan_id} className="border rounded-xl p-4 sm:p-6" data-testid={`loan-${loan.loan_id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                          <div>
                            <h3 className="font-bold text-slate-900">{loan.program_name || 'Prestamo'}</h3>
                            <p className="text-sm text-slate-500">ID: {loan.loan_id?.slice(0, 15)}</p>
                          </div>
                          <Badge className={loan.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                            {loan.status === 'active' ? 'Activo' : loan.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div><p className="text-xs text-slate-500">Monto</p><p className="font-bold">{formatPrice(loan.amount)}</p></div>
                          <div><p className="text-xs text-slate-500">Pago Mensual</p><p className="font-bold text-brand-blue">{formatPrice(loan.monthly_payment)}</p></div>
                          <div><p className="text-xs text-slate-500">Plazo</p><p className="font-bold">{loan.term_months}m</p></div>
                          <div><p className="text-xs text-slate-500">Tasa</p><p className="font-bold">{loan.interest_rate ? `${(loan.interest_rate * 100).toFixed(1)}%` : '4%'} mensual</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <div className="space-y-6">
              {/* My Referral Code */}
              <Card className="bg-gradient-to-r from-brand-blue to-brand-magenta text-white">
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold flex items-center gap-2"><Gift className="w-5 h-5" /> Invita y Gana</h3>
                      <p className="text-white/80 text-sm mt-1">Comparte tu codigo. Tu amigo y tu reciben ${referral?.benefit_per_referral || 500} MXN de descuento.</p>
                    </div>
                    {referral?.code && (
                      <div className="flex items-center gap-2">
                        <div className="bg-white/20 rounded-lg px-4 py-2 font-mono font-bold text-lg" data-testid="referral-code">{referral.code}</div>
                        <Button size="sm" variant="ghost" onClick={copyRefCode} className="text-white hover:bg-white/20" data-testid="copy-referral-btn">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {copied && <p className="text-sm text-white/80 mt-2">Link copiado al portapapeles!</p>}
                  {referral?.referral_link && (
                    <p className="text-xs text-white/60 mt-3 break-all">Link: {referral.referral_link}</p>
                  )}
                </CardContent>
              </Card>

              {/* Referral Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <Card><CardContent className="pt-4 sm:pt-6 text-center"><p className="text-2xl sm:text-3xl font-bold text-brand-blue" data-testid="referral-invited">{referral?.total_referred || 0}</p><p className="text-xs text-slate-600">Invitados</p></CardContent></Card>
                <Card><CardContent className="pt-4 sm:pt-6 text-center"><p className="text-2xl sm:text-3xl font-bold text-green-600" data-testid="referral-enrolled">{referral?.total_enrolled || 0}</p><p className="text-xs text-slate-600">Inscritos</p></CardContent></Card>
                <Card><CardContent className="pt-4 sm:pt-6 text-center"><p className="text-2xl sm:text-3xl font-bold text-brand-magenta">{formatPrice(referral?.total_earnings || 0)}</p><p className="text-xs text-slate-600">Ganado</p></CardContent></Card>
              </div>

              {/* Apply Referral Code */}
              {!referral?.referred_by && (
                <Card>
                  <CardHeader><CardTitle>Tienes un codigo de referido?</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input value={refCode} onChange={e => setRefCode(e.target.value.toUpperCase())} placeholder="EDFI-XXXX-XXXX" className="font-mono" data-testid="apply-ref-input" />
                      <Button onClick={handleApplyRef} disabled={applyingRef || !refCode} className="bg-brand-blue w-full sm:w-auto" data-testid="apply-ref-btn">Aplicar</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {referral?.referred_by && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-bold text-green-800">Fuiste referido con el codigo: {referral.referred_by}</p>
                        <p className="text-sm text-green-600">Descuento de {formatPrice(referral.my_discount)} aplicado</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Referral List */}
              {referral?.referrals && referral.referrals.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Mis Referidos</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {referral.referrals.map((r) => (
                        <div key={r.use_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{r.referred_name}</p>
                            <p className="text-xs text-slate-500">{r.referred_email}</p>
                          </div>
                          <Badge className={r.status === 'enrolled' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                            {r.status === 'enrolled' ? 'Inscrito' : 'Registrado'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default StudentDashboard;
